import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Building2,
  Calendar,
  Layers,
  Sparkles,
  UploadCloud,
  FileSpreadsheet,
  GitCompare,
  FileCheck2,
  Plus,
  LogIn,
  LogOut,
  ChevronDown,
  ExternalLink,
  Sun,
  Moon,
  Receipt,
  Palette,
  FileCode,
  Briefcase,
  Database,
  ShieldAlert
} from 'lucide-react';
import { useGst, FINANCIAL_YEARS, TAX_PERIODS } from '../context/GstContext';
import { toast } from './ui';

export const GlobalHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    clientLegalName,
    isSuperOrCa,
    gstProfiles,
    activeGstin,
    financialYear,
    taxPeriod,
    invoices,
    merchantInvoices,
    activeModule,
    setActiveModule,
    copilotOpen,
    themeMode,
    toggleTheme,
    setCopilotOpen,
    setSecurityModalOpen,
    setActiveGstin,
    setFinancialYear,
    setTaxPeriod,
    setShowProfileModal,
    seedDemoData,
    logout
  } = useGst();

  const [isSeeding, setIsSeeding] = useState<boolean>(false);

  // Determine current active module from path if user navigated directly
  const isBillingRoute = location.pathname.startsWith('/billing');
  const isPracticeRoute = location.pathname.startsWith('/practice');
  const isReturnsRoute = !isBillingRoute && !isPracticeRoute;

  const handleSeedDemo = async () => {
    setIsSeeding(true);
    try {
      await seedDemoData();
      toast.success('Demonstration dataset loaded for Filing & Billing suites!');
    } catch (err: any) {
      toast.error('Failed to seed demo data: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSeeding(false);
    }
  };

  interface NavLinkItem {
    name: string;
    path: string;
    icon: any;
    count?: number;
    badge?: string;
  }

  // Nav links for Tax Returns & Compliance Module
  const returnsNavLinks: NavLinkItem[] = [
    { name: 'Upload Vault', path: '/vault', icon: UploadCloud, count: invoices.length },
    { name: 'Review Ledger', path: '/ledger', icon: FileSpreadsheet, badge: invoices.filter(i => !i.math_verified).length > 0 ? 'Review' : undefined },
    { name: '3-Way Recon & IMS', path: '/reconciliation', icon: GitCompare, badge: invoices.filter(i => i.recon_status === 'MISSING_IN_2B').length > 0 ? 'At Risk' : undefined },
    { name: 'Returns & Annual', path: '/returns', icon: FileCheck2 },
  ];

  // Nav links for Merchant Billing & Invoicing Module
  const billingNavLinks: NavLinkItem[] = [
    { name: 'Merchant Invoices', path: '/billing', icon: Receipt, count: merchantInvoices.length },
    { name: 'Create Invoice', path: '/billing/create', icon: Plus },
    { name: '5 Templates', path: '/billing/templates', icon: Palette },
    { name: 'E-Invoice & EWB', path: '/billing/compliance', icon: FileCode },
  ];

  const currentNavLinks = isPracticeRoute
    ? []
    : isBillingRoute
    ? billingNavLinks
    : returnsNavLinks;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-[rgba(247,247,248,0.08)] bg-[#0e1015]/95 backdrop-blur-md">
      {/* Top Banner / Client Context & Brand */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[rgba(247,247,248,0.05)] text-xs text-[#94a3b8]">
        <div className="flex items-center gap-3">
          <a
            href="https://verveai.co"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 group transition"
            title="Visit Verve AI Labs Sovereign Cloud & AI Systems (verveai.co)"
          >
            <div className="relative h-7 w-7 rounded-xl bg-gradient-to-tr from-[#07080a] via-[#111317] to-[#0e1015] border border-cyan-500/40 p-1 flex items-center justify-center shadow-sm shadow-cyan-500/25 group-hover:border-cyan-400 transition">
              <img
                src="/logo.svg"
                alt="Verve AI Labs"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src.endsWith(".svg")) {
                    target.src = "/logo.png";
                  }
                }}
              />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-white tracking-wide text-xs">
                  Verve <span className="text-cyan-400">AI Labs</span>
                </span>
                <span className="text-[9px] font-mono uppercase px-1 py-0.2 rounded border border-cyan-500/30 bg-cyan-950/60 text-cyan-300">
                  GST
                </span>
              </div>
              <span className="text-[9px] text-slate-500 font-medium -mt-0.5 group-hover:text-cyan-300 transition flex items-center gap-0.5">
                <span>Subsidiary of verveai.co</span>
                <ExternalLink className="h-2 w-2" />
              </span>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-2 pl-4 border-l border-white/10">
            <Building2 className="h-3.5 w-3.5 text-cyan-400" />
            <span className="text-slate-300 font-medium truncate max-w-[200px]">
              {clientLegalName}
            </span>
            {isSuperOrCa && (
              <span className="rounded bg-cyan-950/80 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-300 border border-cyan-800/40">
                CA / ADMIN
              </span>
            )}
          </div>
        </div>

        {/* Auth / Status / Theme Switch Pill & Demo Seeder */}
        <div className="flex items-center gap-2.5">
          {/* 1-Click Load Demo Data */}
          <button
            onClick={handleSeedDemo}
            disabled={isSeeding}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition border border-cyan-500/40 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 shadow-sm"
            title="Load demonstration sales, purchases, and invoices for both Filing & Billing modules"
          >
            <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
            <span>{isSeeding ? 'Loading Demo...' : 'Load Demo Data'}</span>
          </button>

          {/* Theme Mode Toggle (Dark / Light Switch) */}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition border border-white/10 hover:border-cyan-500/40 bg-white/[0.04] text-slate-300 hover:text-white"
            title={themeMode === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
          >
            {themeMode === 'light' ? (
              <>
                <Moon className="h-3.5 w-3.5 text-indigo-500" />
                <span className="hidden sm:inline">Dark</span>
              </>
            ) : (
              <>
                <Sun className="h-3.5 w-3.5 text-amber-400" />
                <span className="hidden sm:inline">Light</span>
              </>
            )}
          </button>

          {/* Security & Cryptographic Trust */}
          <button
            onClick={() => setSecurityModalOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition border border-white/10 hover:border-cyan-500/40 bg-white/[0.04] text-slate-300 hover:text-white"
            title="SOC 2 Type II, ISO 27001 & DPDP 2023 Security Architecture"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-cyan-400" />
            <span className="hidden sm:inline font-mono text-[11px]">DPDP/SOC2</span>
          </button>

          {/* AI Copilot Toggle */}
          <button
            onClick={() => setCopilotOpen(!copilotOpen)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition border ${
              copilotOpen
                ? 'border-cyan-400/80 bg-cyan-950/60 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.35)]'
                : 'border-white/10 hover:border-cyan-500/40 bg-white/[0.04] text-slate-300 hover:text-white'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
            <span className="hidden sm:inline">AI Copilot</span>
          </button>

          {/* Logout button */}
          {user && (
            <button
              onClick={logout}
              className="p-1 rounded-full text-slate-400 hover:text-rose-400 transition ml-1"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TIER 1: Primary Module Navigation Tabs (Dedicated Facility Row)            */}
      {/* ========================================================================= */}
      <div className="flex flex-wrap items-center justify-between px-6 py-2.5 bg-[#090b0e] border-b border-[rgba(247,247,248,0.06)] gap-4">
        {/* Module Switcher Segmented Control */}
        <div className="flex items-center gap-2 p-1 rounded-2xl bg-[#06070a] border border-white/10 shadow-inner">
          {/* Facility 1: Tax Returns & Compliance */}
          <Link
            to="/vault"
            onClick={() => setActiveModule('returns')}
            className={`group flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
              isReturnsRoute
                ? 'bg-gradient-to-r from-emerald-500 to-teal-400 text-slate-950 font-bold shadow-lg shadow-emerald-500/25 ring-1 ring-emerald-300/50'
                : 'text-slate-400 hover:text-emerald-300 hover:bg-emerald-950/25 border border-transparent hover:border-emerald-500/30'
            }`}
            title="GST Returns Filing & 3-Way Reconciliation Suite"
          >
            <div className={`p-1 rounded-lg transition ${isReturnsRoute ? 'bg-black/15 text-slate-950' : 'bg-emerald-500/15 text-emerald-400 group-hover:bg-emerald-500/25'}`}>
              <FileCheck2 className="h-4 w-4" />
            </div>
            <div className="flex flex-col text-left leading-tight">
              <span className="text-xs font-bold tracking-tight">Tax Returns & Compliance</span>
              <span className={`text-[9px] font-normal tracking-wide ${isReturnsRoute ? 'text-emerald-950/80 font-semibold' : 'text-slate-500'}`}>
                GSTR-1, 3B & IMS Recon
              </span>
            </div>
          </Link>

          {/* Facility 2: Merchant Billing & Invoicing */}
          <Link
            to="/billing"
            onClick={() => setActiveModule('billing')}
            className={`group flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
              isBillingRoute
                ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-bold shadow-lg shadow-cyan-500/25 ring-1 ring-cyan-300/50'
                : 'text-slate-400 hover:text-cyan-300 hover:bg-cyan-950/25 border border-transparent hover:border-cyan-500/30'
            }`}
            title="Fast Merchant Billing, 5 GST Templates, E-Invoice & E-Way Bill Suite"
          >
            <div className={`p-1 rounded-lg transition ${isBillingRoute ? 'bg-black/15 text-slate-950' : 'bg-cyan-500/15 text-cyan-400 group-hover:bg-cyan-500/25'}`}>
              <Receipt className="h-4 w-4" />
            </div>
            <div className="flex flex-col text-left leading-tight">
              <span className="text-xs font-bold tracking-tight">Merchant Billing & Invoicing</span>
              <span className={`text-[9px] font-normal tracking-wide ${isBillingRoute ? 'text-cyan-950/80 font-semibold' : 'text-slate-500'}`}>
                Fast Billing, E-Inv & EWB
              </span>
            </div>
          </Link>

          {/* Facility 3: Practice Management (CA / Super Admin Only) */}
          {isSuperOrCa && (
            <Link
              to="/practice"
              onClick={() => setActiveModule('practice')}
              className={`group flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                isPracticeRoute
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-slate-950 font-bold shadow-lg shadow-amber-500/25 ring-1 ring-amber-300/50'
                  : 'text-amber-400/80 hover:text-amber-300 hover:bg-amber-950/25 border border-transparent hover:border-amber-500/30'
              }`}
              title="CA Multi-Client Practice Management Portfolio & Deadlines Command"
            >
              <div className={`p-1 rounded-lg transition ${isPracticeRoute ? 'bg-black/15 text-slate-950' : 'bg-amber-500/15 text-amber-400 group-hover:bg-amber-500/25'}`}>
                <Briefcase className="h-4 w-4" />
              </div>
              <div className="flex flex-col text-left leading-tight">
                <span className="text-xs font-bold tracking-tight">Practice Management</span>
                <span className={`text-[9px] font-normal tracking-wide ${isPracticeRoute ? 'text-amber-950/80 font-semibold' : 'text-amber-500/70'}`}>
                  Super Admin & CA Command
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Active Facility Context Tag */}
        <div className="hidden md:flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${
            isReturnsRoute
              ? 'border-emerald-500/30 bg-emerald-950/40 text-emerald-300'
              : isBillingRoute
              ? 'border-cyan-500/30 bg-cyan-950/40 text-cyan-300'
              : 'border-amber-500/30 bg-amber-950/40 text-amber-300'
          }`}>
            <span className={`h-2 w-2 rounded-full animate-pulse ${
              isReturnsRoute ? 'bg-emerald-400' : isBillingRoute ? 'bg-cyan-400' : 'bg-amber-400'
            }`} />
            <span className="font-semibold">
              {isReturnsRoute
                ? 'Tax Returns & Compliance Suite'
                : isBillingRoute
                ? 'Merchant Billing & Invoicing Suite'
                : 'Super Admin CA Practice Suite'}
            </span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TIER 2: Secondary Contextual Navigation & Statutory Selectors              */}
      {/* Positioned directly BELOW the main tabs so it belongs to the selected tab */}
      {/* ========================================================================= */}
      <div className={`flex flex-wrap items-center justify-between px-6 py-2 gap-4 border-b transition-colors duration-200 ${
        isReturnsRoute
          ? 'border-emerald-500/20 bg-[#070c09]/95'
          : isBillingRoute
          ? 'border-cyan-500/20 bg-[#080d14]/95'
          : 'border-amber-500/20 bg-[#0e0c07]/95'
      }`}>
        {/* Left: Module-specific secondary navigation sub-links */}
        <nav className="flex items-center gap-1.5 overflow-x-auto py-0.5">
          {/* Sub-navigation for Tax Returns & Compliance */}
          {isReturnsRoute && (
            <div className="flex items-center gap-1.5">
              {returnsNavLinks.map((link) => {
                const isActive = location.pathname === link.path;
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition ${
                      isActive
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-semibold shadow-sm'
                        : 'text-slate-300 hover:text-emerald-200 hover:bg-emerald-500/10'
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-emerald-400' : 'text-slate-400'}`} />
                    <span>{link.name}</span>
                    {link.count !== undefined && link.count > 0 && (
                      <span className="ml-1 rounded-full bg-emerald-950 px-1.5 py-0.2 text-[10px] font-mono text-emerald-300 border border-emerald-800/40">
                        {link.count}
                      </span>
                    )}
                    {link.badge && (
                      <span className="ml-1 rounded bg-rose-950/80 px-1.5 py-0.2 text-[9px] font-semibold text-rose-300 border border-rose-800/40">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Sub-navigation for Merchant Billing & Invoicing */}
          {isBillingRoute && (
            <div className="flex items-center gap-1.5">
              {billingNavLinks.map((link) => {
                const isActive = location.pathname === link.path;
                const Icon = link.icon;
                const isCreateAction = link.path === '/billing/create';
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition ${
                      isCreateAction
                        ? 'bg-cyan-500 hover:bg-cyan-400 text-black font-bold shadow-sm shadow-cyan-500/25'
                        : isActive
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 font-semibold shadow-sm'
                        : 'text-slate-300 hover:text-cyan-200 hover:bg-cyan-500/10'
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${isCreateAction ? 'text-black' : isActive ? 'text-cyan-400' : 'text-slate-400'}`} />
                    <span>{link.name}</span>
                    {link.count !== undefined && link.count > 0 && (
                      <span className="ml-1 rounded-full bg-cyan-950 px-1.5 py-0.2 text-[10px] font-mono text-cyan-300 border border-cyan-800/40">
                        {link.count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Sub-navigation for Practice Management */}
          {isPracticeRoute && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                <ShieldAlert className="h-3.5 w-3.5 text-amber-400" />
                <span>Super Admin Practice Command Center (admin@verveailabs.com)</span>
              </div>
              <Link
                to="/practice"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs font-semibold shadow-sm"
              >
                <Briefcase className="h-3.5 w-3.5 text-amber-400" />
                <span>Client Portfolio & Statutory Deadlines</span>
              </Link>
            </div>
          )}
        </nav>

        {/* Right: Statutory Selectors (GSTIN, Year, Month) */}
        <div className="flex items-center gap-2 text-xs">
          {/* Active GSTIN Selector (shown on Returns and Billing) */}
          {!isPracticeRoute && gstProfiles.length > 0 && (
            <div className="relative">
              <select
                value={activeGstin}
                onChange={(e) => setActiveGstin(e.target.value)}
                className="appearance-none rounded-xl border border-white/10 bg-[#131316] py-1.5 pl-3 pr-8 text-xs font-mono font-medium text-white hover:border-cyan-500/40 focus:border-cyan-500 focus:outline-none transition cursor-pointer"
                title="Active Client GSTIN"
              >
                {gstProfiles.map((p) => (
                  <option key={p.gstin} value={p.gstin}>
                    {p.gstin} ({p.stateCode})
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            </div>
          )}

          {/* Financial Year Selector */}
          <div className="relative">
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="appearance-none rounded-xl border border-white/10 bg-[#131316] py-1.5 pl-2.5 pr-7 text-xs font-mono font-medium text-slate-300 hover:border-cyan-500/40 focus:border-cyan-500 focus:outline-none transition cursor-pointer"
              title="Statutory Financial Year"
            >
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          </div>

          {/* Tax Period Selector */}
          <div className="relative">
            <select
              value={taxPeriod}
              onChange={(e) => setTaxPeriod(e.target.value)}
              className="appearance-none rounded-xl border border-white/10 bg-[#131316] py-1.5 pl-2.5 pr-7 text-xs font-medium text-slate-300 hover:border-cyan-500/40 focus:border-cyan-500 focus:outline-none transition cursor-pointer"
              title="Filing Month / Return Period"
            >
              {TAX_PERIODS.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          </div>
        </div>
      </div>
    </header>
  );
};
